/* eslint-disable complexity */
/* eslint-disable sonarjs/cognitive-complexity */
import classNames from 'clsx';
import marchPromo2026Image from 'img/marchPromo2026/quotaBannerImage.png';
import React, { type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { IS_USER_WITH_ONLY_CLOUD, IS_VKT_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { isFamilyQuotaButtonEnabled, isFamilySubsAvailable, quotaTooltip } from 'reactApp/appHelpers/featuresHelpers';
import { cyberMonday2025 } from 'reactApp/appHelpers/featuresHelpers/features/cyberMonday2025';
import { marchPromo2026OverquotaButton } from 'reactApp/appHelpers/featuresHelpers/features/marchPromo2026';
import { mayPromo2025 } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import { abSpaceButton } from 'reactApp/appHelpers/featuresHelpers/features/spaceButton';
import { summerSale2026SidebarButton } from 'reactApp/appHelpers/featuresHelpers/features/summerPromo2026';
import { useMarchPromo2026 } from 'reactApp/hooks/useMarchPromo2026';
import { useUserCanCreateFamilyByBilling } from 'reactApp/modules/billingData/hooks/useUserCanCreateFamilyByBilling';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import {
    getFamilyAlbumsFeature,
    getFamilyFoldersFeature,
    getFamilyLoadingState,
    isFamilyEmpty,
    isUserFamilyOwner,
    userCanCreateFamily,
} from 'reactApp/modules/family/family.selectors';
import { isDefaultRootContent } from 'reactApp/modules/home/home.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { SpaceButtonAnalyticsName } from 'reactApp/ui/Space/SingleSpace/SingleMenu.analytics';
import { SummerSaleButtonAnimation } from 'reactApp/ui/SummerSaleSidebarButton/SummerSaleButtonAnimation';

import styles from '../SingleSpace.css';
import { useQuotaWarning } from './useQuotaWarning';

export const useContent = ({ isMarketingPromo, isSubscriptionsPage }) => {
    const isAlmostFull = useSelector(UserQuotaSelectors.isAlmostFull);
    const isFull = useSelector(UserQuotaSelectors.isFull);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isB2BUser = useSelector(UserSelectors.isB2BUser);
    const isBiz = useSelector(UserSelectors.isBizUser);
    const isDefaultContent = useSelector(isDefaultRootContent);
    const isMobile = EnvironmentSelectors.isPhone();

    const isFamilyOwner = useSelector(isUserFamilyOwner);
    const canCreateFamilyByUser = useSelector(userCanCreateFamily);
    const canCreateFamilyByBilling = useUserCanCreateFamilyByBilling();
    const canCreateFamily = canCreateFamilyByUser && canCreateFamilyByBilling;
    const emptyFamily = useSelector(isFamilyEmpty);

    const { isLoaded: isFamilyLoaded } = useSelector(getFamilyLoadingState);
    const hasFamilyFoldersFeature = useSelector(getFamilyFoldersFeature);
    const hasFamilyAlbumsFeature = useSelector(getFamilyAlbumsFeature);
    const showFamilyFeature = isFamilyLoaded && isFamilyOwner && hasFamilyFoldersFeature && hasFamilyAlbumsFeature;

    const isOverquota = isAlmostFull || isFull;
    const isMySubscription = isPaidUser && !isOverquota;
    const hasEligibleNewFamilyTariffFeature = showFamilyFeature && !abSpaceButton && !isOverquota;
    const showSummerSaleButton = summerSale2026SidebarButton && !isPaidUser && !isBiz && !isOverquota;

    const warning = useQuotaWarning();
    const isMarchPromo2026 = useMarchPromo2026();

    let buttonText: string = 'Увеличить место';
    let analyticsName: SpaceButtonAnalyticsName = SpaceButtonAnalyticsName.SPACE;
    let buttonImage: ReactNode;

    // tempexp_17990-next-line
    if (isMySubscription && !abSpaceButton) {
        buttonText = 'Мои подписки';
        analyticsName = SpaceButtonAnalyticsName.SUBS;
    }

    // tempexp_17990-next-line
    const showFamilyButton =
        !isOverquota && isFamilySubsAvailable && isFamilyQuotaButtonEnabled && (canCreateFamily || (isFamilyOwner && emptyFamily));

    // tempexp_17990-next-line
    if (showFamilyButton && !abSpaceButton) {
        buttonText = 'Поделиться местом';
        analyticsName = SpaceButtonAnalyticsName.FAMILY;
    }

    if (hasEligibleNewFamilyTariffFeature) {
        buttonText = 'Управлять подпиской';
    }

    if (isMarketingPromo) {
        buttonText = 'Увеличить место';
        analyticsName = SpaceButtonAnalyticsName.SPACE;
    }

    if (isMarketingPromo && !isOverquota && !isDefaultContent) {
        if (quotaTooltip === 'b') {
            buttonText = '+1 ТБ за 87 ₽ в месяц';
        } else if (quotaTooltip === 'c' || quotaTooltip === 'd') {
            buttonText = '+256 ГБ за 54 ₽ в месяц';
        }
    }

    if (cyberMonday2025) {
        analyticsName = SpaceButtonAnalyticsName.CYBER_MONDAY_2025;
    }

    if (mayPromo2025 && !isFull) {
        analyticsName = SpaceButtonAnalyticsName.MAY_PROMO_2025;
    }

    if (marchPromo2026OverquotaButton && isMarchPromo2026) {
        analyticsName = SpaceButtonAnalyticsName.MARCH_PROMO_2026;
        buttonImage = (
            <img
                className={classNames(styles.promoImage, { [styles.promoImageMobile]: isMobile })}
                src={marchPromo2026Image}
                alt="Ого-го Облако"
            />
        );
    }

    if (showSummerSaleButton) {
        analyticsName = SpaceButtonAnalyticsName.SUMMER_SALE_2026;
        buttonImage = <SummerSaleButtonAnimation />;
    }

    const buttonState = {
        primary: showSummerSaleButton || (!isMySubscription && !isOverquota),
        // tempexp_17990-next-line
        secondary: isMySubscription && !abSpaceButton,
        error: isOverquota,
    };

    return {
        buttonText,
        buttonImage,
        warning,
        buttonState,
        hideButton: isSubscriptionsPage || isB2BUser || IS_VKT_WEBVIEW,
        // tempexp_17990-next-line
        isMySubscription: !abSpaceButton && isMySubscription && !showFamilyButton,
        hideMenu: (isSubscriptionsPage && IS_USER_WITH_ONLY_CLOUD) || isBiz || IS_VKT_WEBVIEW,
        showFamilyButton,
        analyticsName,
        hasEligibleNewFamilyTariffFeature,
    };
};
